import type { ReactNode } from 'react';
import { useEditorStore, type Tool } from '../../state/editorStore';

const TOOLS: {
  id: Tool;
  tip: string;
  icon: ReactNode;
}[] = [
  {
    id: 'select',
    tip: 'Select (V)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
          d="M3.2 2.2 L3.2 13.2 L6.4 10.1 L8.3 14.2 L10.1 13.4 L8.2 9.3 L12.2 9.1 Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    id: 'blade',
    tip: 'Blade (B)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        {/* Classic double-edge razor blade */}
        <path
          d="M2.2 5.2 H13.8 V10.8 H2.2 Z"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinejoin="round"
        />
        <path
          d="M4.2 5.2 V10.8 M11.8 5.2 V10.8"
          stroke="currentColor"
          strokeWidth="1.15"
          strokeLinecap="round"
        />
        <rect
          x="6.35"
          y="6.85"
          width="3.3"
          height="2.3"
          rx="0.35"
          stroke="currentColor"
          strokeWidth="1.1"
        />
        <path
          d="M2.2 6.6 H4.2 M2.2 9.4 H4.2 M11.8 6.6 H13.8 M11.8 9.4 H13.8"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: 'ripple',
    tip: 'Ripple trim',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <rect x="2" y="4.5" width="5" height="7" rx="0.5" stroke="currentColor" strokeWidth="1.35" />
        <rect x="9" y="4.5" width="5" height="7" rx="0.5" stroke="currentColor" strokeWidth="1.35" />
        <path
          d="M8 3.2 V12.8"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeDasharray="1.5 1.5"
        />
        <path
          d="M5.2 8 H2.8 M13.2 8 H10.8"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: 'hand',
    tip: 'Hand (pan)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
          d="M5.2 7.2 V4.4 C5.2 3.7 5.7 3.2 6.3 3.2 C6.9 3.2 7.4 3.7 7.4 4.4 V6.8"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
        />
        <path
          d="M7.4 6.6 V3.6 C7.4 2.9 7.9 2.4 8.5 2.4 C9.1 2.4 9.6 2.9 9.6 3.6 V6.6"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
        />
        <path
          d="M9.6 6.8 V4.2 C9.6 3.5 10.1 3 10.7 3 C11.3 3 11.8 3.5 11.8 4.2 V8.2"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
        />
        <path
          d="M11.8 7.6 V6.2 C11.8 5.5 12.3 5 12.9 5 C13.5 5 14 5.5 14 6.2 V10.2 C14 12.3 12.3 14 10.2 14 H8.4 C6.6 14 5.1 12.9 4.5 11.3 L3.2 8.2 C2.9 7.5 3.3 6.7 4 6.5 C4.6 6.3 5.2 6.7 5.4 7.2 L5.8 8.2"
          stroke="currentColor"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: 'text',
    tip: 'Text (T)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
          d="M3.5 3.5 H12.5 M8 3.5 V13"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M5.5 13 H10.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

/** Vertical tool rail to the left of the timeline. */
export function TimelineToolsRail() {
  const tool = useEditorStore((s) => s.tool);
  const setTool = useEditorStore((s) => s.setTool);

  return (
    <aside
      className="flex w-9 shrink-0 flex-col items-center gap-0.5 border-r border-hairline bg-panel py-1.5"
      aria-label="Timeline tools"
    >
      {TOOLS.map((t) => {
        const active = tool === t.id;
        return (
          <button
            key={t.id}
            type="button"
            title={t.tip}
            aria-label={t.tip}
            aria-pressed={active}
            onClick={() => setTool(t.id)}
            className={`flex h-8 w-8 items-center justify-center ${
              active
                ? 'bg-raised text-accent'
                : 'text-text-low hover:bg-raised/60 hover:text-text-high'
            }`}
          >
            {t.icon}
          </button>
        );
      })}
    </aside>
  );
}
